import { useState, useEffect, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FiMic, FiMicOff, FiX, FiCommand, FiNavigation, FiPlus, FiAlertCircle, FiLoader, FiCheck } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import { useLanguageStore } from '../../store/languageStore'
import { toast } from 'react-hot-toast'
import { apiClient } from '../../api/client'
import { API_ENDPOINTS } from '../../api/endpoints'
import { destroyNewSalesInvoiceKeepAlive } from '../layout/keepAliveBridge'

// Define the Speech Recognition interface for TS
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList
  resultIndex: number
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean
  interimResults: boolean
  lang: string
  start: () => void
  stop: () => void
  abort: () => void
  onresult: (event: SpeechRecognitionEvent) => void
  onerror: (event: any) => void
  onend: () => void
}

declare global {
  interface Window {
    SpeechRecognition: any
    webkitSpeechRecognition: any
  }
}

export default function VoiceAssistant() {
  const { language } = useLanguageStore()
  const navigate = useNavigate()
  const isRTL = language === 'ar'
  
  const [isListening, setIsListening] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [transcript, setTranscript] = useState('')
  const transcriptRef = useRef('')
  const [isOpen, setIsOpen] = useState(false)
  const [recognition, setRecognition] = useState<SpeechRecognition | null>(null)

  const processCommand = useCallback(async (text: string) => {
    if (!text) return
    const cmd = text.toLowerCase().trim()
    
    // Helper to check multiple keywords
    const matches = (keywords: string[]) => keywords.some(k => cmd.includes(k))
    
    // Helper to extract name/value after a prefix
    const extractAfter = (prefix: string[], input: string) => {
      for (const p of prefix) {
        if (input.includes(p)) {
          return input.split(p)[1]?.trim()
        }
      }
      return null
    }

    const parseNumericFromObject = (obj: Record<string, any>, keyHints: string[]) => {
      const loweredHints = keyHints.map(k => k.toLowerCase())
      for (const [key, value] of Object.entries(obj)) {
        const keyLower = key.toLowerCase()
        if (!loweredHints.some(h => keyLower.includes(h))) continue
        if (typeof value === 'number') return value
        if (typeof value === 'string') {
          const parsed = Number(value)
          if (!Number.isNaN(parsed)) return parsed
        }
      }
      return null
    }

    const extractItemName = (input: string) => {
      const cleaned = input
        .replace(/[؟?]/g, '')
        .replace(/كم|رصيد|صنف|المخزون|stock|item|balance|of|for/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim()
      return cleaned
    }

    const handleStockInquiry = async (itemName: string) => {
      if (!itemName) {
        toast.error(isRTL ? 'اذكر اسم الصنف بعد الأمر' : 'Please mention item name')
        return
      }
      try {
        const itemsRes = await apiClient.get(API_ENDPOINTS.items.list, {
          params: { page: 1, pageSize: 10, searchTerm: itemName }
        })
        const rows = Array.isArray(itemsRes.data?.data)
          ? itemsRes.data.data
          : (Array.isArray(itemsRes.data) ? itemsRes.data : [])
        if (!rows.length) {
          toast.error(isRTL ? `لم أجد الصنف: ${itemName}` : `Item not found: ${itemName}`)
          return
        }

        const matched = rows.find((it: any) => {
          const ar = String(it.name ?? '').toLowerCase()
          const en = String(it.nameEn ?? '').toLowerCase()
          return ar.includes(itemName.toLowerCase()) || en.includes(itemName.toLowerCase())
        }) ?? rows[0]

        const itemId = String(matched.id ?? matched.ID ?? '')
        if (!itemId) {
          toast.error(isRTL ? 'تعذر تحديد معرف الصنف' : 'Could not resolve item id')
          return
        }

        const stockRes = await apiClient.get(API_ENDPOINTS.invoices.itemStock(itemId))
        const payload = stockRes.data ?? {}
        const stockValue =
          (typeof payload === 'number' ? payload : null) ??
          parseNumericFromObject(payload, ['stock', 'qty', 'quantity', 'available', 'balance']) ??
          (Array.isArray(payload) && payload.length
            ? parseNumericFromObject(payload[0], ['stock', 'qty', 'quantity', 'available', 'balance'])
            : null)

        if (stockValue == null) {
          toast.success(
            isRTL
              ? `تم العثور على الصنف "${matched.name ?? itemName}" لكن لم أستطع قراءة الرصيد تلقائياً.`
              : `Found "${matched.name ?? itemName}", but couldn't parse stock value.`
          )
          return
        }

        toast.success(
          isRTL
            ? `رصيد الصنف "${matched.name ?? itemName}" هو ${stockValue}`
            : `Stock balance for "${matched.name ?? itemName}" is ${stockValue}`
        )
      } catch {
        toast.error(isRTL ? 'فشل الاستعلام عن رصيد الصنف' : 'Failed to fetch item stock')
      }
    }

    const handleTodaySalesInquiry = async () => {
      try {
        const summaryRes = await apiClient.get(API_ENDPOINTS.dashboard.summary())
        const summary = summaryRes.data ?? {}
        const salesToday =
          parseNumericFromObject(summary, ['todaysales', 'salesToday', 'today_sale', 'sale_today']) ??
          parseNumericFromObject(summary, ['todayrevenue', 'revenueToday', 'today_revenue']) ??
          parseNumericFromObject(summary, ['totalsales', 'sales'])

        if (salesToday == null) {
          toast.success(
            isRTL
              ? 'تم فتح لوحة المعلومات، لكن لم أجد حقلاً واضحاً لإجمالي مبيعات اليوم.'
              : 'Opened dashboard, but could not detect a clear today sales field.'
          )
          navigate('/dashboard')
          return
        }

        toast.success(
          isRTL ? `إجمالي مبيعات اليوم: ${salesToday}` : `Today sales total: ${salesToday}`
        )
      } catch {
        toast.error(isRTL ? 'فشل جلب إجمالي مبيعات اليوم' : 'Failed to fetch today sales total')
      }
    }

    // Arabic Commands Logic
    if (language === 'ar') {
      // Direct business commands
      if (matches(['افتح فاتورة مبيعات جديدة', 'فاتورة مبيعات جديدة', 'انشاء فاتورة مبيعات', 'إنشاء فاتورة مبيعات'])) {
        destroyNewSalesInvoiceKeepAlive()
        navigate('/invoices/new')
        toast.success('جاري فتح فاتورة مبيعات جديدة')
        setIsOpen(false)
        return
      }

      if (matches(['إجمالي مبيعات اليوم', 'كم مبيعات اليوم', 'مبيعات اليوم'])) {
        await handleTodaySalesInquiry()
        setIsOpen(false)
        return
      }

      if (matches(['كم رصيد صنف', 'رصيد صنف', 'رصيد الصنف', 'كم مخزون صنف', 'مخزون صنف'])) {
        const rawName = extractAfter(['صنف', 'الصنف'], cmd) || extractItemName(cmd)
        await handleStockInquiry(rawName)
        setIsOpen(false)
        return
      }

      // 0. Smart Creation Commands (Enhanced)
      if (matches(['إضافة مورد باسم', 'اضافة مورد باسم', 'انشاء مورد باسم', 'إنشاء مورد باسم'])) {
        const name = extractAfter(['باسم'], cmd)
        if (name) {
          navigate('/vendors', { state: { autoOpen: true, initialName: name } })
          toast.success(`جاري فتح شاشة إضافة المورد: ${name}`)
          setIsOpen(false)
          return
        }
      }
      
      if (matches(['إضافة عميل باسم', 'اضافة عميل باسم', 'انشاء عميل باسم', 'إنشاء عميل باسم'])) {
        const name = extractAfter(['باسم'], cmd)
        if (name) {
          navigate('/customers', { state: { autoOpen: true, initialName: name } })
          toast.success(`جاري فتح شاشة إضافة العميل: ${name}`)
          setIsOpen(false)
          return
        }
      }

      if (matches(['عمل قيد بقيمة', 'انشاء قيد بقيمة', 'إنشاء قيد بقيمة', 'قيد محاسبي بقيمة'])) {
        const amountStr = extractAfter(['بقيمة'], cmd)
        const amount = parseFloat(amountStr || '')
        if (!isNaN(amount)) {
          navigate('/journal/new', { state: { initialAmount: amount } })
          toast.success(`جاري إنشاء قيد محاسبي بقيمة: ${amount}`)
          setIsOpen(false)
          return
        }
      }

      // 1. Restaurant & Cafe
      else if (matches(['مطعم', 'كافيه', 'مقهى', 'طاولات', 'طاولة', 'مطبخ', 'شاشة المطبخ', 'توصيل', 'دليفري', 'قائمة الطعام', 'منيو'])) {
        if (matches(['كاشير', 'نقطة بيع'])) navigate('/pos')
        else if (matches(['طاولات', 'طاولة'])) navigate('/tables')
        else if (matches(['مطبخ', 'شاشة'])) navigate('/kitchen')
        else if (matches(['توصيل', 'دليفري'])) navigate('/restaurant/delivery')
        else if (matches(['دور', 'انتظار'])) navigate('/restaurant/orders-queue')
        else if (matches(['قائمة', 'منيو', 'إعداد'])) navigate('/restaurant/menu-setup')
        else navigate('/pos')
        toast.success('فتح نظام المطاعم...')
      }

      // 2. Stores & Inventory
      else if (matches(['مخزن', 'مخازن', 'مستودع', 'مستودعات', 'جرد', 'تسوية', 'باركود', 'أدوية'])) {
        if (matches(['فئة', 'فئات'])) navigate('/categories')
        else if (matches(['صنف', 'أصناف', 'منتج'])) navigate('/items')
        else if (matches(['سعر', 'أسعار', 'تعديل'])) navigate('/items/prices-editor')
        else if (matches(['دليل'])) navigate('/items-directory')
        else if (matches(['تحويل', 'مناقلة'])) navigate('/branch-transfers')
        else if (matches(['تسوية', 'تعديل'])) navigate('/inventory-adjustments')
        else if (matches(['إقفال', 'اقفال'])) navigate('/periodic-inventory/close')
        else if (matches(['أول المدة', 'اول المدة'])) navigate('/opening-stock-invoices')
        else if (matches(['دواء', 'أدوية', 'صيدلية'])) navigate('/medicines')
        else if (matches(['طباعة', 'باركود'])) navigate('/barcode-print')
        else navigate('/stores')
        toast.success('فتح إدارة المخازن...')
      }

      // 3. Accounts & Finance
      else if (matches(['حسابات', 'شجرة', 'دليل الحسابات', 'مركز تكلفة', 'ميزانية', 'سند', 'صرف', 'قبض', 'أصول'])) {
        if (matches(['شجرة', 'دليل'])) navigate('/chart-of-accounts')
        else if (matches(['مركز', 'تكلفة'])) navigate('/cost-centers')
        else if (matches(['ميزانية', 'موازنة'])) navigate('/budgets')
        else if (matches(['نوع', 'أنواع', 'قيد'])) navigate('/operation-types')
        else if (matches(['قيد', 'قيود'])) navigate('/journal')
        else if (matches(['كل', 'جميع'])) navigate('/all-journal-entries')
        else if (matches(['دوري', 'تكرار'])) navigate('/recurring-journal-entries')
        else if (matches(['سند', 'صرف', 'قبض'])) navigate('/vouchers')
        else if (matches(['أصول', 'اصل'])) navigate('/fixed-assets')
        else navigate('/chart-of-accounts')
        toast.success('فتح النظام المالي...')
      }

      // 4. HR & Employees
      else if (matches(['موظف', 'موظفين', 'هيكل', 'قسم', 'أقسام', 'عقد', 'عقود', 'إجازة', 'اجازة', 'حضور', 'انصراف', 'دوام', 'وردية'])) {
        if (matches(['موظف', 'موظفين'])) navigate('/hr/employees')
        else if (matches(['هيكل', 'قسم', 'أقسام'])) navigate('/hr/departments')
        else if (matches(['مسمى', 'وظيفي'])) navigate('/hr/job-profiles')
        else if (matches(['عقد', 'عقود'])) navigate('/hr/contracts')
        else if (matches(['إجازة', 'اجازات'])) navigate('/hr/leave-requests')
        else if (matches(['حضور', 'انصراف', 'بصمة', 'بريك', 'استراحة'])) {
          if (matches(['جهاز', 'ماكينة'])) navigate('/hr/attendance-devices')
          else if (matches(['موقع', 'لوكيشن', 'جي بي اس', 'gps'])) navigate('/hr/attendance-app')
          else if (matches(['سجل', 'بصمات', 'بريك', 'استراحة'])) navigate('/hr/attendance-punches')
          else navigate('/hr/attendance')
        }
        else if (matches(['سياسة', 'سياسات'])) navigate('/hr/attendance-policies')
        else if (matches(['جدول', 'جداول', 'وردية'])) navigate('/hr/work-schedules')
        else if (matches(['ساعة', 'ساعات'])) navigate('/hr/time-entries')
        else navigate('/hr/dashboard')
        toast.success('فتح الموارد البشرية...')
      }

      // 5. Municipality Module
      else if (matches(['بلدية', 'شكوى', 'شكاوى', 'مواطن', 'سكن', 'مجلس', 'لجنة', 'مشروع', 'إيراد', 'رخصة', 'ترخيص', 'وثيقة'])) {
        if (matches(['شكوى', 'شكاوى'])) navigate('/municipality/complaints')
        else if (matches(['مواطن', 'مواطنين'])) navigate('/municipality/citizens')
        else if (matches(['سكن', 'وحدة'])) navigate('/municipality/housing-units')
        else if (matches(['مجلس', 'لجنة', 'لجان'])) navigate('/municipality/committees')
        else if (matches(['مشروع', 'مشاريع'])) navigate('/municipality/projects')
        else if (matches(['إيراد', 'جبايات'])) navigate('/municipality/revenues')
        else if (matches(['رخصة', 'ترخيص'])) navigate('/municipality/licenses')
        else if (matches(['وثيقة', 'وثائق'])) navigate('/municipality/official-documents')
        else if (matches(['راتب', 'رواتب'])) navigate('/municipality/salaries')
        else if (matches(['مالي', 'موازنة'])) navigate('/municipality/budgets')
        else if (matches(['إنجاز', 'انجاز'])) navigate('/municipality/achievements')
        else navigate('/municipality/dashboard')
        toast.success('فتح بوابة البلدية...')
      }

      // 6. Maintenance Management
      else if (matches(['صيانة', 'فني', 'ورشة', 'إصلاح', 'امر عمل', 'أمر عمل'])) {
        if (matches(['فني', 'فنيين'])) navigate('/maintenance/technicians')
        else if (matches(['امر', 'أمر', 'طلب'])) navigate('/maintenance/work-orders')
        else if (matches(['إعدادات', 'اعدادات'])) navigate('/maintenance/settings')
        else navigate('/maintenance/work-orders')
        toast.success('فتح إدارة الصيانة...')
      }

      // 7. Sales & CRM
      else if (matches(['مبيعات', 'فاتورة', 'فواتير', 'طلب بيع', 'عرض سعر', 'مرتجع', 'ولاء', 'خصم', 'عرض', 'مندوب'])) {
        if (matches(['فاتورة', 'فواتير'])) navigate('/invoices')
        else if (matches(['طلب', 'امر'])) navigate('/sales-orders')
        else if (matches(['عرض', 'سعر'])) navigate('/quotations')
        else if (matches(['مرتجع'])) navigate('/sales-returns')
        else if (matches(['ولاء', 'نقاط'])) navigate('/loyalty')
        else if (matches(['خصم', 'عروض'])) navigate('/promotions')
        else if (matches(['مندوب', 'مناديب'])) navigate('/sales-reps')
        else if (matches(['وردية', 'ورديات'])) navigate('/shifts')
        else navigate('/invoices')
        toast.success('فتح إدارة المبيعات...')
      }

      // 8. Purchase Management
      else if (matches(['مشتريات', 'طلب شراء', 'فاتورة شراء', 'مرتجع شراء'])) {
        if (matches(['طلب', 'امر'])) navigate('/purchase-orders')
        else if (matches(['فاتورة', 'فواتير'])) navigate('/purchase-invoices')
        else if (matches(['مرتجع'])) navigate('/purchase-returns')
        else navigate('/purchase-invoices')
        toast.success('فتح إدارة المشتريات...')
      }

      // 9. Production & Manufacturing
      else if (matches(['تكلفة الإنتاج', 'تكلفة انتاج', 'احتساب تكلفة', 'حاسبة تكلفة'])) {
        navigate('/production/cost-calculations')
        toast.success('فتح احتساب تكلفة الإنتاج...')
      }
      else if (matches(['إنتاج', 'انتاج', 'تصنيع', 'وصفة', 'مواد'])) {
        if (matches(['وصفة', 'مكونات'])) navigate('/production/boms')
        else if (matches(['امر', 'أمر', 'طلب'])) navigate('/production/orders')
        else navigate('/production/orders')
        toast.success('فتح نظام الإنتاج...')
      }

      // 10. Reports & Analytics
      else if (matches(['تقرير', 'تقارير', 'كشف', 'مصمم', 'تحليل'])) {
        if (matches(['مصمم'])) navigate('/reports/designer')
        else if (matches(['تفاعلي', 'بفوت', 'pivot'])) navigate('/reports/pivot')
        else navigate('/reports')
        toast.success('فتح التقارير...')
      }

      // 11. Portal & External
      else if (matches(['بوابة', 'بورتال', 'عميل', 'مناقصة', 'مناقصات'])) {
        if (matches(['مناقصة', 'مناقصات'])) navigate('/tenders')
        else navigate('/portal/dashboard')
        toast.success('فتح البوابة الخارجية...')
      }

      // 12. Settings & System
      else if (matches(['إعدادات', 'ضبط', 'تجهيز', 'لوحة التحكم', 'الرئيسية'])) {
        if (matches(['لوحة', 'رئيسية'])) navigate('/dashboard')
        else navigate('/settings')
        toast.success('فتح الإعدادات...')
      }
      else {
        toast.error('عذراً، لم أفهم هذا الأمر. حاول قول: "افتح نظام المطاعم" أو "إضافة مورد جديد"')
      }
    } 
    // English Commands Logic
    else {
      if (matches(['open new sales invoice', 'new sales invoice', 'create sales invoice'])) {
        destroyNewSalesInvoiceKeepAlive()
        navigate('/invoices/new')
        toast.success('Opening new sales invoice...')
      }
      else if (matches(['today sales', 'sales today', 'today total sales'])) {
        await handleTodaySalesInquiry()
      }
      else if (matches(['stock balance', 'item balance', 'stock of item', 'item stock'])) {
        const itemName = extractItemName(cmd)
        await handleStockInquiry(itemName)
      }
      else
      if (matches(['restaurant', 'cafe', 'pos', 'table', 'kitchen', 'delivery', 'menu'])) {
        if (matches(['pos', 'cashier'])) navigate('/pos')
        else if (matches(['table'])) navigate('/tables')
        else if (matches(['kitchen'])) navigate('/kitchen')
        else if (matches(['delivery'])) navigate('/restaurant/delivery')
        else navigate('/pos')
        toast.success('Opening Restaurant System...')
      }
      else if (matches(['store', 'inventory', 'item', 'product', 'category', 'price', 'barcode'])) {
        if (matches(['category'])) navigate('/categories')
        else if (matches(['item', 'product'])) navigate('/items')
        else if (matches(['price'])) navigate('/items/prices-editor')
        else if (matches(['barcode'])) navigate('/barcode-print')
        else navigate('/stores')
        toast.success('Opening Inventory...')
      }
      else if (matches(['account', 'finance', 'chart', 'ledger', 'journal', 'voucher', 'asset'])) {
        if (matches(['chart', 'tree'])) navigate('/chart-of-accounts')
        else if (matches(['journal'])) navigate('/journal')
        else if (matches(['voucher'])) navigate('/vouchers')
        else if (matches(['asset'])) navigate('/fixed-assets')
        else navigate('/chart-of-accounts')
        toast.success('Opening Finance...')
      }
      else if (matches(['hr', 'employee', 'payroll', 'attendance', 'leave', 'contract'])) {
        if (matches(['employee'])) navigate('/hr/employees')
        else if (matches(['attendance'])) {
          if (matches(['site', 'location', 'gps'])) navigate('/hr/attendance-app')
          else navigate('/hr/attendance')
        }
        else if (matches(['leave'])) navigate('/hr/leave-requests')
        else navigate('/hr/dashboard')
        toast.success('Opening HR...')
      }
      else if (matches(['sale', 'invoice', 'quotation', 'return', 'loyalty', 'promo'])) {
        if (matches(['invoice'])) navigate('/invoices')
        else if (matches(['quotation'])) navigate('/quotations')
        else if (matches(['return'])) navigate('/sales-returns')
        else navigate('/invoices')
        toast.success('Opening Sales...')
      }
      else if (matches(['purchase', 'vendor', 'supplier'])) {
        if (matches(['invoice'])) navigate('/purchase-invoices')
        else if (matches(['vendor', 'supplier'])) navigate('/vendors')
        else navigate('/purchase-invoices')
        toast.success('Opening Purchases...')
      }
      else if (matches(['municipality', 'complaint', 'citizen', 'project', 'license'])) {
        navigate('/municipality/dashboard')
        toast.success('Opening Municipality Module...')
      }
      else if (matches(['maintenance', 'technician', 'work order'])) {
        navigate('/maintenance/work-orders')
        toast.success('Opening Maintenance...')
      }
      else if (matches(['report', 'pivot', 'designer'])) {
        if (matches(['pivot'])) navigate('/reports/pivot')
        else navigate('/reports')
        toast.success('Opening Reports...')
      }
      else if (matches(['setting', 'config', 'dashboard', 'home'])) {
        if (matches(['dashboard', 'home'])) navigate('/dashboard')
        else navigate('/settings')
        toast.success('Opening Settings...')
      }
      else {
        toast.error("Command not recognized. Try 'Open Invoices' or 'New Vendor'.")
      }
    }

    setIsOpen(false)
    setIsListening(false)
    setIsProcessing(false)
    setTranscript('')
    transcriptRef.current = ''
  }, [navigate, language, isRTL])

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    if (SpeechRecognition) {
      const rec = new SpeechRecognition()
      rec.continuous = false
      rec.interimResults = true
      rec.lang = language === 'ar' ? 'ar-SA' : 'en-US'

      rec.onresult = (event: SpeechRecognitionEvent) => {
        let interimTranscript = ''
        let finalTranscript = ''

        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript
          } else {
            interimTranscript += event.results[i][0].transcript
          }
        }

        const currentText = finalTranscript || interimTranscript
        if (currentText) {
          setTranscript(currentText)
          transcriptRef.current = currentText
        }
      }

      rec.onerror = (event: any) => {
        console.error('Speech recognition error', event.error)
        setIsListening(false)
        setIsProcessing(false)
        if (event.error === 'not-allowed') {
          toast.error(language === 'ar' ? 'يرجى السماح بالوصول للميكروفون' : 'Please allow microphone access')
        }
      }

      rec.onend = () => {
        setIsListening(false)
        if (transcriptRef.current && isOpen) {
          setIsProcessing(true)
          // Give user a moment to see the final text before execution
          setTimeout(() => {
            processCommand(transcriptRef.current)
          }, 1200)
        }
      }

      setRecognition(rec)
    }
  }, [language, processCommand, isOpen])

  const toggleListening = () => {
    if (isListening) {
      recognition?.stop()
      setIsListening(false)
    } else {
      setTranscript('')
      transcriptRef.current = ''
      setIsProcessing(false)
      recognition?.start()
      setIsListening(true)
      setIsOpen(true)
    }
  }

  if (!window.SpeechRecognition && !window.webkitSpeechRecognition) {
    return null
  }

  return (
    <>
      {/* Floating Mic Button */}
      <div className={`fixed bottom-12 ${isRTL ? 'right-6' : 'left-6'} z-50 flex flex-col items-center gap-2`}>
        <AnimatePresence>
          {!isListening && !isProcessing && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 10 }}
              className="px-2 py-1 rounded-lg bg-gray-900/80 backdrop-blur-md border border-white/10 shadow-xl"
            >
              <span className="text-[9px] font-bold text-white uppercase tracking-widest whitespace-nowrap">
                {isRTL ? 'المساعد الذكي' : 'AI Assistant'}
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.button
          whileHover={{ scale: 1.1, boxShadow: '0 10px 30px rgba(99, 102, 241, 0.3)' }}
          whileTap={{ scale: 0.9 }}
          onClick={toggleListening}
          className={`relative w-12 h-12 rounded-full flex items-center justify-center shadow-xl transition-all duration-500 group overflow-hidden ${
            isListening ? 'bg-rose-500 shadow-rose-500/30' : isProcessing ? 'bg-emerald-500 shadow-emerald-500/30' : 'bg-indigo-600 shadow-indigo-600/30'
          }`}
          style={{
            border: '1.5px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          {isProcessing ? (
            <FiLoader className="text-white animate-spin text-lg" />
          ) : (
            <FiMic className={`text-white text-lg ${isListening ? 'animate-pulse' : ''}`} />
          )}
          
          {/* Inner Glow */}
          <div className="absolute inset-0 rounded-full border border-white/10 pointer-events-none" />
        </motion.button>
      </div>

      {/* Voice Assistant UI Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className={`fixed bottom-28 ${isRTL ? 'right-6' : 'left-6'} z-50 w-72 rounded-[1.5rem] shadow-2xl overflow-hidden`}
            style={{
              background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)',
              backdropFilter: 'blur(15px)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            <div className="p-6">
              <div className={`flex justify-between items-center mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${isListening ? 'bg-rose-500/20' : isProcessing ? 'bg-emerald-500/20' : 'bg-indigo-500/20'}`}>
                    {isProcessing ? (
                      <FiCheck className="text-emerald-400" />
                    ) : (
                      <FiMic className={`${isListening ? 'text-rose-400 animate-pulse' : 'text-indigo-400'}`} />
                    )}
                  </div>
                  <h3 className="text-white font-black text-xs uppercase tracking-widest">
                    {isRTL ? 'المساعد الصوتي الذكي' : 'AI Voice Assistant'}
                  </h3>
                </div>
                <button onClick={() => {
                  setIsOpen(false)
                  setIsListening(false)
                  setIsProcessing(false)
                  recognition?.stop()
                }} className="text-white/30 hover:text-white transition-colors">
                  <FiX />
                </button>
              </div>

              <div className="min-h-[100px] flex flex-col items-center justify-center text-center">
                {isListening ? (
                  <>
                    <p className="text-rose-400 font-bold text-lg mb-2 animate-pulse">
                      {isRTL ? 'جاري الاستماع...' : 'Listening...'}
                    </p>
                    <p className="text-white/90 text-lg font-medium leading-relaxed">
                      "{transcript || (isRTL ? 'تحدث الآن' : 'Speak now...')}"
                    </p>
                  </>
                ) : isProcessing ? (
                  <>
                    <p className="text-emerald-400 font-bold text-lg mb-2">
                      {isRTL ? 'جاري التنفيذ...' : 'Executing...'}
                    </p>
                    <p className="text-white/70 text-sm font-medium italic mb-4">
                      "{transcript}"
                    </p>
                    <FiLoader className="text-emerald-400 animate-spin text-xl" />
                  </>
                ) : (
                  <div className="space-y-4">
                    <p className="text-white/40 text-xs leading-relaxed">
                      {isRTL 
                        ? 'تحدث بوضوح وسأقوم بتنفيذ طلبك بعد الانتهاء' 
                        : 'Speak clearly and I will execute your request after you finish'}
                    </p>
                    <p className="text-indigo-400/60 text-[10px] uppercase font-bold tracking-tighter">
                      {isRTL 
                        ? 'مثال: "إضافة مورد باسم شركة الأمل" أو "عمل قيد بقيمة 5000"' 
                        : 'Example: "Add vendor named Hope Co" or "Make journal entry with 5000"'}
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-6 pt-6 border-t border-white/5 grid grid-cols-2 gap-3">
                <div className={`p-3 rounded-2xl bg-white/5 flex flex-col items-center gap-2 transition-colors ${isListening ? 'opacity-100' : 'opacity-40'}`}>
                  <FiNavigation className="text-sky-400" />
                  <span className="text-[10px] font-black text-white/40 uppercase">{isRTL ? 'تنقل' : 'Navigate'}</span>
                </div>
                <div className={`p-3 rounded-2xl bg-white/5 flex flex-col items-center gap-2 transition-colors ${isListening ? 'opacity-100' : 'opacity-40'}`}>
                  <FiPlus className="text-emerald-400" />
                  <span className="text-[10px] font-black text-white/40 uppercase">{isRTL ? 'إضافة' : 'Create'}</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
